"use client";
import { usePathname } from "next/navigation";
import SectionTitle from "./SectionTitle";

type MaxWidth =
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "4xl"
  | "5xl"
  | "6xl"
  | "7xl"
  | "full";

type SectionTitleInputProps = {
  title?: string;
  description?: string[];
  align?: "left" | "center" | "right";
  titleMaxWidth?: MaxWidth;
  descriptionMaxWidth?: MaxWidth;
};

// Right column items now support optional heading + text
export type RightColumnItem = {
  heading?: string;
  text: string;
};

export type BDLCSectionProps = {
  sectionTitle?: SectionTitleInputProps;
  leftColumnItems?: string[];
  rightColumnItems?: RightColumnItem[];
  dual?: boolean; // ✅ new prop to toggle 2-column vs 1-column
};

export default function BDLCSection({
  sectionTitle,
  leftColumnItems = [],
  rightColumnItems = [],
  dual = false,
}: BDLCSectionProps) {
  const pathname = usePathname();
  const isSeasiaCaseStudy = pathname === "/seasia-casestudy";

  return (
    <section className="relative lg:block section-background-filler py-20">
      <div className="custom-container">
        <div className="xl:grid xl:grid-cols-12 gap-12 items-start">
          {/* Left Column */}
          <div className="xl:col-span-7 z-50">
            <div className="z-20">
              <div className="max-w-full xl:max-w-lg">
                <SectionTitle
                  title={sectionTitle?.title ?? ""}
                  {...(sectionTitle?.description && { description: sectionTitle.description })}
                  align={"left"}
                  titleMaxWidth={"full"}
                  descriptionMaxWidth={"full"}
                />
              </div>

              {!isSeasiaCaseStudy && leftColumnItems?.length > 0 && (
                <p className="text-xl font-semibold text-[#1b1b1b] my-6">
                  Key problems to solve:
                </p>
              )}

              <div
                className={`grid grid-cols-1 gap-y-6 mt-4 ${dual ? "xl:grid-cols-2 gap-x-12" : ""
                  }`}
              >
                {leftColumnItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div className="relative size-6 rounded-full bg-white border-2 border-[#d53333] flex items-center justify-center shadow-sm">
                        <div className="w-2 h-2 rounded-full bg-red-600"></div>
                      </div>
                    </div>
                    <p className="text-[#1b1b1b] text-lg leading-snug xl:max-w-lg">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Decorative background */}
          <div
            className="hidden xl:block w-2/3 absolute top-0 left-0 h-full bg-gray-100/50 backdrop-blur-sm z-10"
            style={{ clipPath: "polygon(0 0, 68% 0, 100% 100%, 0% 100%)" }}
          ></div>

          <div
            className="hidden xl:block w-2/3 absolute top-0 -left-10 h-full bg-gray-100/50 backdrop-blur-sm z-10"
            style={{ clipPath: "polygon(0 0, 68% 0, 100% 100%, 0% 100%)" }}
          ></div>

          {/* Right Column */}
          <div className="col-span-12 xl:col-span-5 mt-8 xl:mt-0 xl:-ml-10 z-90 relative">
            <p className="text-2xl xl:text-3xl font-bold text-[#1b1b1b] xl:my-6 my-4">
              Core Gaps Identified:
            </p>

            <div
              className="
                flex flex-col relative py-4 xl:py-12
                xl:transform xl:skew-x-20 2xl:skew-x-24
              "
            >
              {rightColumnItems.map((item, i) => (
                <div
                  key={i}
                  className="
                    flex mb-4 xl:mb-10 items-center relative gap-6
                    xl:transform xl:-skew-x-20 2xl:-skew-x-24
                  "
                >
                  <div className="flex flex-col items-center">
                    <div className="relative size-7 xl:size-12 rounded-full bg-white shadow-xl flex items-center justify-center">
                      <div className="size-2 xl:size-3 rounded-full bg-red-600"></div>
                    </div>
                    {i < rightColumnItems.length - 1 && (
                      <div className="flex-1 w-0.5 bg-gray-300 mt-1"></div>
                    )}
                  </div>

                  <div>
                    {/* Heading optional */}
                    {item.heading && (
                      <h4 className="text-base xl:text-lg font-semibold text-[#272d57] mb-1">
                        {item.heading}
                      </h4>
                    )}
                    <p className="text-[#1b1b1b] text-lg leading-snug">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
